package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.layout.engine.Measurer
import top.wcpe.allincore.ui.runtime.reactive.Effect
import top.wcpe.allincore.ui.runtime.theme.ControlTheme
import top.wcpe.allincore.ui.runtime.theme.ThemeAccess
import kotlin.jvm.JvmStatic

/**
 * 文本元素。
 */
@Suppress("TooManyFunctions", "MagicNumber")
class Text private constructor() : UIElement<Text>() {

    private var textField = ""
    private var colorField = 0xFFFFFFFF.toInt()
    private var sizeField = 1f
    private var sizeExplicitField = false
    private var typographyField: TextTypography? = null
    private var textOverrideColorField: Int? = null
    private var multilineField = false

    /** 富文本开关：true 时整段交平台按内联富文本（[icon:xx] 标签/图文混排/§ 颜色码）解析绘制。 */
    private var richTextField = false
    private var textAlignField = "left"

    /** 文本溢出处理（如 "ellipsis" / "clip"），与 maxLines 配合多行截断。 */
    private var textOverflowField: String? = null

    /** 最大行数（null = 不限）。 */
    private var maxLinesField: Int? = null

    /** 逐字乱码揭示开关。 */
    private var decipherField = false

    /** 乱码揭示时长（毫秒）。 */
    private var decipherDurationMsField = 800f

    /** 乱码揭示字符集（null = 渲染层默认）。 */
    private var decipherCharsetField: String? = null

    /** 乱码揭示起始时刻（毫秒，-1 = 尚未锁定，首帧渲染时设为 now）。 */
    private var decipherStartMsField = -1L

    /** 乱码揭示次序缓存（按文本长度计算一次的固定随机排列，文本变化时失效）。 */
    private var decipherOrderField: IntArray? = null

    /** decipherOrder 对应的文本长度，用于侦测文本变化后重算次序。 */
    private var decipherOrderLengthField = -1

    /** 字间距（像素，按 scale 缩放）。null = 回退默认整串绘制。 */
    private var letterSpacingField: Float? = null

    /** 多行文本行高（行间 y 步进，像素）。null = 回退 effectiveLineHeight。 */
    private var lineHeightField: Float? = null

    /** 文本装饰线："none"/"underline"/"line-through"/"underline line-through"。null = 无装饰。 */
    private var textDecorationField: String? = null

    // width / height 都保持 auto；由 measurer 根据当前 text/size 动态推导内禀尺寸，
    // 防止叶子节点在 FLEX 容器中坍缩到 0 导致命中盒丢失。

    override fun self(): Text = this

    fun text(text: String?): Text = apply {
        textField = text ?: ""
        markLayoutDirty() // FR-30 B1：文字变化影响 measurer 测尺
    }

    /**
     * 把文本绑定到响应式供给器：依赖变化时自动刷新。
     * 内部建立的 [Effect] 注册到本元素的 [lifecycleScope]，元素 dispose 时一并释放。
     */
    fun bindText(supplier: () -> String): Text = apply {
        lifecycleScopeField.register(
            Effect {
                textField = normalize(supplier())
                markLayoutDirty() // FR-30 B1：响应式文字变化影响 measurer 测尺（不经 PropAppliers）
            },
        )
    }

    fun color(argb: Int): Text = apply { colorField = argb }

    fun size(size: Float): Text = apply {
        // 仅记录字号，layout 高度由 measurer 根据 size 动态返回。
        sizeField = size
        sizeExplicitField = true
        markLayoutDirty() // FR-30 B1：字号影响 measurer 测尺
    }

    fun typography(typography: TextTypography?): Text = apply { typographyField = typography }

    internal fun textOverrideColor(argb: Int?): Text = apply {
        textOverrideColorField = argb
        markRenderDirty()
    }

    /** 读取声明的文字颜色覆盖（未覆盖返回 null）；动画起点读取用此原始字段而非 [color] 解析色。 */
    internal fun textOverrideColor(): Int? = textOverrideColorField

    fun multiline(v: Boolean): Text = apply {
        multilineField = v
        markLayoutDirty() // FR-30 B1：多行开关影响 measurer 测尺
    }
    fun multiline(): Boolean = multilineField

    /** 富文本开关：true 时按内联富文本解析（[icon:xx] 标签/图文混排/§ 颜色码）；false 时走普通文本路径（零回归）。 */
    fun richText(v: Boolean): Text = apply { richTextField = v }
    fun richText(): Boolean = richTextField

    fun textAlign(v: String?): Text = apply { textAlignField = v ?: "left" }
    fun textAlign(): String = textAlignField

    fun textOverflow(v: String?): Text = apply { textOverflowField = v }
    fun textOverflow(): String? = textOverflowField

    fun maxLines(v: Int?): Text = apply { maxLinesField = v }
    fun maxLines(): Int? = maxLinesField

    /** 字间距（像素，按 scale 缩放）；null/0 时走原整串绘制（零回归）。 */
    fun letterSpacing(v: Float?): Text = apply { letterSpacingField = v }
    fun letterSpacing(): Float? = letterSpacingField

    /** 多行行高（行间 y 步进，像素）；null 时回退 effectiveLineHeight。 */
    fun lineHeight(v: Float?): Text = apply {
        lineHeightField = v
        markLayoutDirty() // FR-30 B1：行高影响 measurer 测尺
    }
    fun lineHeight(): Float? = lineHeightField

    /** 文本装饰线（underline / line-through / 二者组合）；null 时无装饰。 */
    fun textDecoration(v: String?): Text = apply { textDecorationField = v }
    fun textDecoration(): String? = textDecorationField

    fun decipher(v: Boolean): Text = apply { decipherField = v }
    fun decipher(): Boolean = decipherField

    fun decipherDurationMs(v: Float): Text = apply { decipherDurationMsField = v }
    fun decipherDurationMs(): Float = decipherDurationMsField

    fun decipherCharset(v: String?): Text = apply { decipherCharsetField = v }
    fun decipherCharset(): String? = decipherCharsetField

    /** 乱码揭示起始时刻（-1 = 尚未锁定）。 */
    fun decipherStartMs(): Long = decipherStartMsField

    /** 渲染层首帧锁定揭示起始时刻。 */
    fun decipherStartMs(ms: Long) {
        decipherStartMsField = ms
    }

    /** 当前缓存的揭示次序（可能为 null）。 */
    fun decipherOrder(): IntArray? = decipherOrderField

    /** 渲染层缓存揭示次序，并记录其对应的文本长度以便后续侦测文本变化。 */
    fun decipherOrder(order: IntArray?) {
        decipherOrderField = order
        decipherOrderLengthField = order?.size ?: -1
    }

    /** 当前缓存次序对应的文本长度（-1 = 未缓存）。 */
    fun decipherOrderLength(): Int = decipherOrderLengthField
    // 字体样式（font / font(FontStyle)）已上移到 UIElement 基类，所有带文字控件共用。

    fun text(): String = textField

    fun color(): Int = textOverrideColorField ?: colorField

    fun size(): Float = sizeField

    fun sizeExplicit(): Boolean = sizeExplicitField

    fun typography(): TextTypography? = typographyField

    /**
     * 返回最终像素字号：typography token 存储的是 px，显式 size 则沿用 RenderBridge scale 语义并换算成 px。
     */
    fun effectivePixelSize(theme: ControlTheme?): Float {
        val typo = typographyField
        return if (sizeExplicitField || typo == null) {
            BASE_LINE_HEIGHT * sizeField
        } else {
            typo.metrics(resolveTheme(theme)).size
        }
    }

    /**
     * 返回 RenderBridge 需要的相对 scale；typography px token 以 vanilla 9px 字高为基准换算。
     */
    fun effectiveScale(theme: ControlTheme?): Float = effectivePixelSize(theme) / BASE_LINE_HEIGHT

    /** 保留旧命名兼容，请使用 effectiveScale。 */
    @Deprecated("保留旧命名兼容，请使用 effectiveScale", ReplaceWith("effectiveScale(theme)"))
    fun effectiveSize(theme: ControlTheme?): Float = effectiveScale(theme)

    fun effectiveLineHeight(theme: ControlTheme?): Float {
        val typo = typographyField
        return if (typo != null && !sizeExplicitField) {
            typo.metrics(resolveTheme(theme)).lineHeight
        } else {
            BASE_LINE_HEIGHT * sizeField
        }
    }

    /**
     * Text 的内禀尺寸由当前 `text` 与有效字号决定：
     * - 单行模式：宽度 = `字符数 * 6 * effectiveScale`，高度 = `effectiveLineHeight`
     * - 多行模式：按已知宽度 kw 自动换行估算行数，宽度取各行最大像素宽，高度 = `行数 * effectiveLineHeight`
     *
     * 用户显式 `width` / `height` 时，layout 引擎以显式值为准——measurer 只在维度为 `auto` 时被调用。
     */
    override fun measurer(): Measurer = Measurer { kw, _ ->
        val theme = ThemeAccess.current()
        val scale = effectiveScale(theme)
        val lineHeight = effectiveLineHeight(theme)
        val charWidth = BASE_GLYPH_WIDTH * scale
        if (!multilineField) {
            floatArrayOf((textField.length * charWidth).coerceAtLeast(0f), lineHeight)
        } else {
            measureMultiline(kw, charWidth, lineHeight)
        }
    }

    /** 多行模式内禀尺寸：按 \n 分段，再按 kw 自动换行估算行数与最大行宽。 */
    private fun measureMultiline(kw: Float, charWidth: Float, lineHeight: Float): FloatArray {
        if (textField.isEmpty()) {
            return floatArrayOf(0f, lineHeight)
        }
        val paragraphs = textField.split("\n")

        // 无已知宽度时，仅按显式 \n 统计行数
        if (kw.isNaN() || kw <= 0f) {
            val maxWidth = paragraphs.maxOf { it.length * charWidth }
            return floatArrayOf(maxWidth, paragraphs.size * lineHeight)
        }

        // 有已知宽度时，逐段逐字符估算换行（与 TextRenderer + ButtonRenderer.wrapText 同逻辑）
        var totalLines = 0
        var maxWidth = 0f
        for (paragraph in paragraphs) {
            if (paragraph.isEmpty()) {
                totalLines++
                continue
            }
            var start = 0
            while (start < paragraph.length) {
                var accWidth = 0f
                var end = start
                while (end < paragraph.length) {
                    if (accWidth + charWidth > kw && end > start) {
                        break
                    }
                    accWidth += charWidth
                    end++
                }
                maxWidth = maxOf(maxWidth, accWidth)
                totalLines++
                start = end
            }
        }
        return floatArrayOf(maxWidth, totalLines * lineHeight)
    }

    private fun normalize(value: String?): String = value ?: ""

    companion object {
        /** Minecraft vanilla Font.lineHeight，size=1.0 时的默认行高。 */
        private const val BASE_LINE_HEIGHT = 9f

        /** size=1.0 时按字符估算的平均像素宽（Minecraft 字体约 6px / 字）。 */
        private const val BASE_GLYPH_WIDTH = 6f

        @JvmStatic
        fun create(): Text = Text()

        private fun resolveTheme(theme: ControlTheme?): ControlTheme = theme ?: ControlTheme.defaultFallback()
    }
}
